/** Geometry for deciding how many modifier pills fit inline before the readout strip overflows. */
export interface FitInput {
  /** Inner width available to the strip (px); the container's measured clientWidth. */
  avail: number;
  /** Width of the leading anchor (the selected key + "+"); always shown, never dropped. */
  anchorW: number;
  /** Each pill's measured width (px), in display order (already sorted by modifier weight). */
  pillWidths: number[];
  /** Width of the "+N more" chip, reserved only when at least one pill is dropped. */
  moreW: number;
  /** Horizontal gap between flex items (px). */
  gap: number;
}

/**
 * How many pills fit inline after the anchor. Showing the first k pills costs the anchor plus
 * `gap + width` per pill, plus `gap + moreW` for the "+N more" chip WHEN k < total (a dropped tail
 * needs the chip; showing all of them does not). We take the largest k that fits - so the chip is
 * never reserved against the full set, and the last pill can fit precisely because no chip is owed.
 * A non-positive `avail` (the row's non-shrinking left blocks consumed everything) fits no pill:
 * the anchor and the "+N more" chip are the only survivors, keeping the tail reachable. The
 * no-layout pre-measure fallback (SSR, happy-dom) lives in ReadoutStrip, keyed on the ROW having
 * no width at all - a caller with a laid-out row always gets the honest squeeze here.
 */
export function fitInlineCount({ avail, anchorW, pillWidths, moreW, gap }: FitInput): number {
  const total = pillWidths.length;
  if (avail <= 0) return 0;
  const widthFor = (k: number): number => {
    let w = anchorW;
    for (let i = 0; i < k; i++) w += gap + (pillWidths[i] ?? 0);
    if (k < total) w += gap + moreW; // a dropped tail is shown as the "+N more" chip
    return w;
  };
  for (let k = total; k >= 0; k--) {
    if (widthFor(k) <= avail) return k;
  }
  return 0;
}
